import styles from "./FolderBrowser.module.css";

/**
 * Shared file-row presentation helpers used by both the folder browser
 * (FolderBrowser) and the Knowledge Base search results (KnowledgeSearchResults)
 * so file icons, labels, sizes, and relative dates render identically in both.
 */

export function getFileExtension(name: string): string {
  if (!name) return "";
  const parts = name.split(".");
  return parts.length > 1 ? (parts[parts.length - 1] ?? "").toLowerCase() : "";
}

export type FileIconKind = "pdf" | "doc" | "xls" | "ppt" | "img" | "txt" | "csv" | "other";

export function getFileIconKind(ext: string): FileIconKind {
  if (ext === "pdf") return "pdf";
  if (ext === "doc" || ext === "docx") return "doc";
  if (ext === "xls" || ext === "xlsx") return "xls";
  if (ext === "ppt" || ext === "pptx") return "ppt";
  if (ext === "png" || ext === "jpg" || ext === "jpeg" || ext === "gif") return "img";
  if (ext === "txt") return "txt";
  if (ext === "csv") return "csv";
  return "other";
}

export function getFileIconLabel(ext: string): string {
  const kind = getFileIconKind(ext);
  if (kind === "other") return ext.toUpperCase().substring(0, 3) || "FILE";
  return kind.toUpperCase();
}

export function getFileIconClass(kind: FileIconKind): string {
  if (kind === "pdf") return styles.iconPdf!;
  if (kind === "doc") return styles.iconDoc!;
  if (kind === "xls") return styles.iconXls!;
  if (kind === "ppt") return styles.iconPpt!;
  if (kind === "img") return styles.iconImg!;
  if (kind === "txt") return styles.iconTxt!;
  if (kind === "csv") return styles.iconCsv!;
  return styles.iconOther!;
}

export function formatFileSize(bytes: number): string {
  if (!bytes) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
}

/**
 * Accessible pastel background + dark text pairs for tag chips. Kept small and
 * high-contrast so any tag reads clearly on a white card in either theme.
 */
const TAG_PALETTE: ReadonlyArray<{ backgroundColor: string; color: string }> = [
  { backgroundColor: "#e5efff", color: "#0b4f9e" }, // blue
  { backgroundColor: "#e2f5e9", color: "#1c6b3a" }, // green
  { backgroundColor: "#f1e9fc", color: "#5b2ca6" }, // purple
  { backgroundColor: "#fdefd6", color: "#8a5300" }, // amber
  { backgroundColor: "#fde7f0", color: "#a01e5e" }, // pink
  { backgroundColor: "#dcf4f1", color: "#0d6a60" }, // teal
  { backgroundColor: "#fde6e6", color: "#a52121" }, // red
  { backgroundColor: "#e7e9fc", color: "#33339c" }, // indigo
];

/**
 * Deterministic colour for a tag: the same tag always gets the same swatch, so a
 * given term (e.g. "SOP") reads consistently across every card and the filter
 * chips. Returns the pastel `{ backgroundColor, color }` for a chip; pass
 * `active` for the solid, high-emphasis variant used by a selected filter chip.
 */
export function getTagStyle(tag: string, active = false): { backgroundColor: string; color: string } {
  let hash = 0;
  for (const ch of tag) hash = (hash * 31 + (ch.codePointAt(0) ?? 0)) >>> 0;
  const swatch = TAG_PALETTE[hash % TAG_PALETTE.length]!;
  return active ? { backgroundColor: swatch.color, color: "#ffffff" } : swatch;
}

export function formatRelativeDate(dateStr: string): string {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  const diffMs = Date.now() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins} min ago`;
  const diffHrs = Math.floor(diffMins / 60);
  if (diffHrs < 24) return `${diffHrs}h ago`;
  const diffDays = Math.floor(diffHrs / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString();
}
